import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useGetStockTransferByIdQuery } from '../../../store/api/inventoryApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function StockTransferDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: transfer, isLoading } = useGetStockTransferByIdQuery(id);
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  if (isLoading) return <Typography>Loading...</Typography>;
  if (!transfer) return <Typography>Transfer not found</Typography>;

  const formatAmount = (value) => {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: company?.currencyDecimals ?? 2,
    }).format(Number(value || 0));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
        <IconButton onClick={() => navigate('/inventory/transfers')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Stock Transfer Details
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                  <Typography variant="h6" color="primary">{transfer.transferNo}</Typography>
                  <Typography color="text.secondary">Date: {new Date(transfer.transferDate).toLocaleDateString()}</Typography>
                </Box>
                <Chip
                  label={transfer.status}
                  color={transfer.status === 'completed' ? 'success' : 'default'}
                />
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">From Location</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{transfer.fromLocation?.name}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">To Location</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{transfer.toLocation?.name}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>Transfer Items</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Batch No</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Unit Cost</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Total Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transfer.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.product?.name}
                          </Typography>
                          {item.product?.sku && (
                            <Typography variant="caption" color="text.secondary">
                              SKU: {item.product?.sku}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.batchNo ? (
                            <Chip label={item.batchNo} size="small" variant="outlined" color="primary" />
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>{item.product?.baseUnit || '-'}</TableCell>
                        <TableCell align="right">
                          {formatAmount(item.unitCost)}
                        </TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatAmount(item.totalCost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Summary</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Total Items</Typography>
                <Typography>{transfer.items?.length || 0}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Total Quantity</Typography>
                <Typography>
                  {transfer.items?.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Total Value</Typography>
                <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {formatAmount(transfer.totalAmount)}
                </Typography>
              </Box>

              {transfer.notes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Notes</Typography>
                  <Typography variant="body2">{transfer.notes}</Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default StockTransferDetail;
